import React from 'react';
import { Box, Container, Typography, Button, Card, CardContent, Divider, useMediaQuery, useTheme } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';

export default function LoginPin() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const apiBase = (() => {
    if (typeof window === 'undefined') return '';
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocal ? 'http://localhost:8080/api' : '/api';
  })();

  const loginGoogle = () => {
    const redirect = typeof window !== 'undefined' ? window.location.origin + '/me' : '/me';
    window.location.href = `${apiBase}/oauth2/authorization/google?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 50px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f6fb 0%, #ffffff 100%)',
        py: { xs: 4, sm: 6 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="lg">
        <Card
          sx={{
            maxWidth: isMobile ? '100%' : 900,
            margin: '0 auto',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(17,24,39,.12)',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          }}
        >
          {/* Left side - Branding */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
              color: 'white',
              padding: { xs: 4, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '50%',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  display: 'inline-block',
                  background: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  backdropFilter: 'blur(4px)',
                  mb: 3,
                }}
              >
                TIKRAI
              </Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontSize: { xs: '28px', sm: '36px' },
                  fontWeight: 900,
                  lineHeight: 1.2,
                  mb: 2,
                }}
              >
                Sveiki sugrįžę
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.95,
                  fontSize: { xs: '15px', sm: '16px' },
                  lineHeight: 1.6,
                  mb: 4,
                }}
              >
                Prisijunkite, kad galėtumėte sekti kainas, sutaupyti ir valdyti savo paskyrą.
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.9,
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                🔒 Saugus prisijungimas per patikimus tiekėjus
              </Typography>
            </Box>
          </Box>

          {/* Right side - Login Form */}
          <CardContent
            sx={{
              padding: { xs: 4, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontSize: { xs: '24px', sm: '28px' },
                fontWeight: 900,
                mb: 1,
                color: '#111827',
              }}
            >
              Prisijungimas
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                mb: 4,
                fontSize: '15px',
              }}
            >
              Pasirinkite jums patogiausią būdą.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                onClick={loginGoogle}
                variant="outlined"
                fullWidth
                startIcon={<GoogleIcon />}
                sx={{
                  padding: '14px 20px',
                  borderRadius: 2,
                  borderColor: '#e5e7eb',
                  color: '#111827',
                  fontWeight: 700,
                  fontSize: '15px',
                  textTransform: 'none',
                  backgroundColor: '#fff',
                  '&:hover': {
                    borderColor: '#d1d5db',
                    backgroundColor: '#f9fafb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  },
                }}
              >
                Prisijungti su Google
              </Button>

              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" sx={{ color: '#9ca3af', px: 2 }}>
                  arba
                </Typography>
              </Divider>

              <Box
                sx={{
                  background: '#f9fafb',
                  borderRadius: 2,
                  padding: 2,
                  border: '1px solid #e5e7eb',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    fontSize: '13px',
                    lineHeight: 1.6,
                  }}
                >
                  Prisijungę galėsite išsaugoti produktus, sekti kainų pokyčius ir gauti įspėjimus.
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: '#9ca3af',
                fontSize: '12px',
                mt: 4,
                textAlign: 'center',
                display: 'block',
              }}
            >
              Tęsdami sutinkate su mūsų{' '}
              <Box component="a" href="/api/taisykles.html" sx={{ color: '#2e7d32', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Naudojimo taisyklėmis
              </Box>{' '}
              ir{' '}
              <Box component="a" href="/api/privacy-policy.html" sx={{ color: '#2e7d32', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Privatumo politika
              </Box>
              .
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}